import { useEffect, useState } from "react";
import { FileText, Printer } from "lucide-react";
import { api, API, getToken, formatApiErrorDetail } from "@/lib/api";
import { ImpactTrendChart } from "@/components/ImpactTrendChart";

const fmt = (v, suffix = "") => (v === null || v === undefined ? "—" : `${v.toLocaleString()}${suffix}`);

const STATS = [
  ["homes_tested", "Homes tested"],
  ["systems_installed", "Systems installed"],
  ["avg_reduction_pct", "Avg. radon reduction", "%"],
  ["homes_above_action", "Homes at or above 4.0"],
  ["readings", "Readings logged"],
  ["households_qualified", "Likely to qualify for help"],
  ["library_loans", "Library kit loans"],
  ["zip_codes", "ZIP codes reached"],
];

const download = async (path, filename) => {
  const qs = new URLSearchParams({ app_url: window.location.origin });
  const r = await fetch(`${API}${path}?${qs}`, { headers: { Authorization: `Bearer ${getToken()}` } });
  if (!r.ok) throw new Error(`Download failed (HTTP ${r.status})`);
  const url = URL.createObjectURL(await r.blob());
  const a = document.createElement("a");
  a.href = url; a.download = filename; a.click();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
};

const ReportButton = ({ icon: Icon, label, busyLabel, path, filename, testid, primary, onError }) => {
  const [busy, setBusy] = useState(false);
  const go = async () => {
    setBusy(true);
    onError("");
    try { await download(path, filename); } catch (e) { onError(e.message); } finally { setBusy(false); }
  };
  return (
    <button onClick={go} disabled={busy} data-testid={testid}
      className={`${primary ? "btn-primary" : "btn-secondary"} !py-2 !px-4 text-sm inline-flex items-center gap-2 disabled:opacity-60`}>
      <Icon size={14} /> {busy ? busyLabel : label}
    </button>
  );
};

export const ImpactReports = () => {
  const [stats, setStats] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    api.get("/admin/impact").then(
      (r) => setStats(r.data),
      (e) => setError(formatApiErrorDetail(e.response?.data?.detail) || e.message),
    );
  }, []);

  return (
    <div className="card-flat fade-up delay-1" data-testid="admin-impact">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <p className="eyebrow">Live impact</p>
          <h3 className="font-serif text-2xl mt-1">What the program has achieved</h3>
          <p className="text-xs text-[#5C6661] mt-1">Staff accounts excluded. Updates every time you open this page.</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <ReportButton icon={FileText} label="Funder report (PDF)" busyLabel="Preparing…" primary
            path="/admin/reports/funder.pdf" filename="Radon_Action_Tracking_Funder_Report.pdf"
            testid="admin-download-funder-report" onError={setError} />
          <ReportButton icon={Printer} label="Library & school flyer · EN / ES (PDF)" busyLabel="Preparing…"
            path="/admin/reports/flyer.pdf" filename="Radon_Action_Tracking_Flyer.pdf"
            testid="admin-download-flyer" onError={setError} />
        </div>
      </div>

      {error && <p className="text-xs text-[#9B2C2C] mt-3" data-testid="admin-impact-error">{error}</p>}

      <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-[#E8ECEA] border border-[#E8ECEA] rounded-xl overflow-hidden mt-5">
        {STATS.map(([key, label, suffix]) => (
          <div key={key} className="bg-white p-4" data-testid={`impact-${key}`}>
            <p className="font-serif text-3xl text-[#1C201F]">{stats ? fmt(stats[key], suffix) : "…"}</p>
            <p className="text-xs text-[#5C6661] mt-1">{label}</p>
          </div>
        ))}
      </div>
      {stats?.verified_systems > 0 && (
        <p className="text-xs text-[#5C6661] mt-3" data-testid="impact-before-after">
          Verified homes went from an average of <b className="text-[#1C201F]">{stats.avg_pre_pci} pCi/L</b> to{" "}
          <b className="text-[#1C201F]">{stats.avg_post_pci} pCi/L</b> after mitigation.
        </p>
      )}
      {stats?.trend && <ImpactTrendChart trend={stats.trend} />}
    </div>
  );
};
